import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
    collection,
    getDocs,
    doc,
    setDoc,
    query,
    where
} from "firebase/firestore";
import { db } from "../firebase";
import { addDays, format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import { LinearProgress } from "@mui/material";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

/* ===== CONSTANTS ===== */
const COL_WIDTH = 120;
const LEFT_COL_WIDTH = 260;
const PAGE_SIZE = 7;

function Rateupdates() {
    const auth = getAuth();

    const [userId, setUserId] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    const [rooms, setRooms] = useState([]);
    const [plans, setPlans] = useState({});
    const [roomPlans, setRoomPlans] = useState([]);

    const [rates, setRates] = useState({});
    const [availability, setAvailability] = useState({});
    const [savingRoomId, setSavingRoomId] = useState(null);

    const [dates, setDates] = useState([]);
    const [page, setPage] = useState(0);

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;
            setUserId(user.uid);

            const snap = await getDocs(collection(db, "authroles"));
            snap.forEach(d => {
                if (d.id === user.uid) {
                    setPropertyId(d.data().propertyId);
                }
            });
        });
        return () => unsub();
    }, []);

    /* ================= LOAD DATA ================= */
    useEffect(() => {
        if (!userId || !propertyId) return;
        loadAll();
    }, [userId, propertyId]);

    const loadAll = async () => {
        /* ROOMS */
        const roomSnap = await getDocs(collection(db, "rooms"));
        setRooms(
            roomSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(r => r.userId === userId && r.propertyId === propertyId)
        );

        /* RATE PLANS */
        const planSnap = await getDocs(collection(db, "rate_plans"));
        const planMap = {};
        planSnap.docs.forEach(d => {
            planMap[d.id] = d.data().planName;
        });
        setPlans(planMap);

        /* ROOM ↔ PLAN */
        const tariffSnap = await getDocs(collection(db, "room_tariffs"));
        setRoomPlans(
            tariffSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(t => t.userId === userId && t.propertyId === propertyId)
        );

        /* EXISTING RATES */
        const rateSnap = await getDocs(
            query(
                collection(db, "room_rates_calendar"),
                where("userId", "==", userId),
                where("propertyId", "==", propertyId)
            )
        );
        const rateMap = {};
        rateSnap.docs.forEach(d => {
            const r = d.data();
            rateMap[`${r.roomId}_${r.ratePlanId}_${r.date}`] = r.saleRate;
        });
        setRates(rateMap);

        /* AVAILABILITY */
        const availSnap = await getDocs(
            query(
                collection(db, "room_availability_calendar"),
                where("userId", "==", userId),
                where("propertyId", "==", propertyId)
            )
        );
        const availMap = {};
        availSnap.docs.forEach(d => {
            const a = d.data();
            availMap[`${a.roomId}_${a.date}`] = a.quantity;
        });
        setAvailability(availMap);

    };

    /* ================= DATES ================= */
    useEffect(() => {
        const arr = [];
        for (let i = 0; i < 180; i++) {
            arr.push(addDays(new Date(), i));
        }
        setDates(arr);
    }, []);

    const visibleDates = dates.slice(
        page * PAGE_SIZE,
        page * PAGE_SIZE + PAGE_SIZE
    );

    /* ================= CHANGE HANDLERS ================= */
    const changeQty = (roomId, date, val) => {
        setAvailability(p => ({ ...p, [`${roomId}_${date}`]: val }));
    };

    const changeRate = (roomId, planId, date, val) => {
        setRates(p => ({ ...p, [`${roomId}_${planId}_${date}`]: val }));
    };

    /* ================= BULK UPDATE ================= */
    const bulkQty = async (roomId) => {
        const { value } = await Swal.fire({
            title: "Bulk Availability",
            input: "number",
            inputLabel: "Available rooms for all future dates",
            showCancelButton: true,
            background: colors.calPolyGreen,
            color: colors.vanilla,
            confirmButtonColor: colors.goldenrod,
            cancelButtonColor: colors.darkGreen
        });
        if (value === undefined) return;

        const updated = { ...availability };
        dates.forEach(d => {
            updated[`${roomId}_${format(d, "yyyy-MM-dd")}`] = value;
        });
        setAvailability(updated);
        toast.success("Bulk availability applied");
    };

    const bulkRate = async (roomId, planId) => {
        const { value } = await Swal.fire({
            title: "Bulk Rate",
            input: "number",
            inputLabel: "Rate for all future dates",
            showCancelButton: true,
            background: colors.calPolyGreen,
            color: colors.vanilla,
            confirmButtonColor: colors.goldenrod,
            cancelButtonColor: colors.darkGreen
        });
        if (value === undefined) return;

        const updated = { ...rates };
        dates.forEach(d => {
            updated[`${roomId}_${planId}_${format(d, "yyyy-MM-dd")}`] = value;
        });
        setRates(updated);
    };

    /* ================= SAVE ================= */
    const saveRoom = async (roomId) => {
        try {
            setSavingRoomId(roomId);

            for (let d of dates) {
                const date = format(d, "yyyy-MM-dd");

                const plansForRoom = roomPlans.filter(p => p.roomId === roomId);

                for (let p of plansForRoom) {
                    const rateKey = `${roomId}_${p.ratePlanId}_${date}`;

                    if (!rates[rateKey]) continue;

                    await setDoc(doc(db, "room_rates_calendar", rateKey), {
                        roomId,
                        ratePlanId: p.ratePlanId,
                        planName: plans[p.ratePlanId],
                        date,
                        saleRate: Number(rates[rateKey]),
                        userId,
                        propertyId,
                        updatedAt: new Date()
                    });
                }
            }

            toast.success("Rates saved successfully");
        } catch (err) {
            toast.error("Save failed");
        } finally {
            setSavingRoomId(null);
        }
    };

    const isWeekend = (date) => {
        const day = format(date, "EEE");
        return day === "Sat" || day === "Sun";
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
                <ToastContainer autoClose={2000} />

                <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }}>
                    Room Availability & Rates
                </Typography>

                <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardContent sx={{ p: 3 }}>

                        {/* NAV */}
                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <IconButton disabled={page === 0} onClick={() => setPage(p => p - 1)} sx={{ color: colors.vanilla }}>
                                <ArrowBack />
                            </IconButton>
                            <IconButton
                                disabled={(page + 1) * PAGE_SIZE >= dates.length}
                                onClick={() => setPage(p => p + 1)}
                                sx={{ color: colors.vanilla }}
                            >
                                <ArrowForward />
                            </IconButton>
                        </Box>

                        {/* GRID */}
                        <Box sx={{ overflowX: "auto" }}>
                            {/* HEADER */}
                            <Box sx={{ display: "flex", position: "sticky", top: 0, zIndex: 2, bgcolor: colors.darkGreen }}>
                                <Box sx={{ minWidth: LEFT_COL_WIDTH, p: 1, fontWeight: 700, color: colors.vanilla }}>
                                    Room / Plan
                                </Box>
                                {visibleDates.map(d => {
                                    const weekend = isWeekend(d);

                                    return (
                                        <Box
                                            key={d.toISOString()}
                                            sx={{
                                                width: COL_WIDTH,
                                                textAlign: "center",
                                                p: 1,
                                                bgcolor: weekend ? colors.darkGreen : colors.calPolyGreen,
                                                borderLeft: `1px solid rgba(222,226,177,0.1)`
                                            }}
                                        >
                                            <Typography
                                                fontSize={12}
                                                fontWeight={600}
                                                sx={{ color: weekend ? colors.gold : colors.goldenrod }}
                                            >
                                                {format(d, "EEE")}
                                            </Typography>
                                            <Typography
                                                fontSize={13}
                                                fontWeight={700}
                                                sx={{ color: colors.vanilla }}
                                            >
                                                {format(d, "dd MMM")}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* BODY */}
                            {rooms.map(room => {
                                const plansForRoom = roomPlans.filter(p => p.roomId === room.id);

                                return (
                                    <Box key={room.id} sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                        {/* RATES */}
                                        {plansForRoom.map(p => (
                                            <Box key={p.id} sx={{ display: "flex", borderBottom: `1px solid rgba(222,226,177,0.05)`, alignItems: "center", bgcolor: colors.darkGreen }}>
                                                <Box sx={{ minWidth: LEFT_COL_WIDTH, p: 1.5, color: colors.vanilla }}>
                                                    <Typography fontWeight={600} sx={{ color: colors.gold, fontSize: '0.9rem' }}>
                                                        {room.roomTypeName} - {plans[p.ratePlanId]}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        onClick={() => bulkRate(room.id, p.ratePlanId)}
                                                        sx={{ 
                                                            mt: 1, 
                                                            bgcolor: colors.goldenrod, 
                                                            color: colors.darkGreen, 
                                                            fontWeight: 700, 
                                                            textTransform: "none", 
                                                            borderRadius: "6px",
                                                            fontSize: '0.75rem',
                                                            py: 0.5,
                                                            '&:hover': { bgcolor: colors.gold } 
                                                        }}
                                                    >
                                                        Bulk Rate
                                                    </Button>
                                                </Box>
                                                {visibleDates.map(d => {
                                                    const date = format(d, "yyyy-MM-dd");
                                                    return (
                                                        <Box key={date} sx={{ width: COL_WIDTH, p: 0.5, borderLeft: `1px solid rgba(222,226,177,0.1)` }}>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                fullWidth
                                                                inputProps={{ style: { color: colors.vanilla, textAlign: 'center' } }}
                                                                value={rates[`${room.id}_${p.ratePlanId}_${date}`] || ""}
                                                                onChange={e =>
                                                                    changeRate(room.id, p.ratePlanId, date, e.target.value)
                                                                }
                                                                sx={{ 
                                                                    "& .MuiOutlinedInput-root": { 
                                                                        backgroundColor: colors.calPolyGreen,
                                                                        "& fieldset": { borderColor: colors.goldenrod },
                                                                        "&:hover fieldset": { borderColor: colors.gold },
                                                                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                                                                    } 
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        ))}

                                        <Box textAlign="right" my={1.5} pr={1}>
                                            <Button
                                                variant="contained"
                                                disabled={savingRoomId === room.id}
                                                onClick={() => saveRoom(room.id)}
                                                sx={{ 
                                                    minWidth: 180,
                                                    bgcolor: colors.goldenrod, 
                                                    color: colors.darkGreen, 
                                                    fontWeight: 700, 
                                                    textTransform: "none", 
                                                    borderRadius: "8px",
                                                    '&:hover': { bgcolor: colors.gold } 
                                                }}
                                            >
                                                {savingRoomId === room.id ? "Saving..." : `Save ${room.roomTypeName}`}
                                            </Button>

                                            {savingRoomId === room.id && (
                                                <LinearProgress
                                                    sx={{ 
                                                        mt: 1, 
                                                        borderRadius: 1, 
                                                        backgroundColor: colors.calPolyGreen,
                                                        '& .MuiLinearProgress-bar': { backgroundColor: colors.gold }
                                                    }}
                                                />
                                            )}
                                        </Box>

                                    </Box>
                                );
                            })}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Rateupdates;